import type { AxiosInstance } from "axios";
import { caseTransformingAxios } from "../../shared/v2/caseTransformingAxios";
import { getExternalApi } from "../../shared/externalApi";

const PLACEHOLDERS_URL = "/text_templates/placeholders";
const PARSE_URL = "/text_templates/parse";

export const MAX_NAME_LENGTH = 100;
export const MAX_TEMPLATE_LENGTH = 1600;
export const RECORDS_PER_PAGE = 25;

interface MessagingPage<T> {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  items: T[];
}

/** `TextTemplateDTO` returned by the messaging service. */
export interface TextTemplate {
  uuid: string;
  createdByUserUuid: string;
  organizationUuid: string;
  templateName: string;
  template: string;
  isShared: boolean;
  dateCreated: string;
  dateUpdated: string | null;
}

/** A template being edited, or a new (unsaved) template that only has a name and template. */
export type TextTemplateDraft = Pick<TextTemplate, "templateName" | "template"> & Partial<TextTemplate>;

export interface Pagination {
  currentPage: number;
  perPage: number;
  totalPages: number;
  totalEntries: number;
}

export interface TextTemplatesResponse {
  textTemplates: TextTemplate[];
  pagination: Pagination;
}

export interface TextTemplatesParams {
  page?: number;
  perPage?: number;
  search?: string;
}

export const MAX_SEARCH_LENGTH = 100;

export interface Placeholder {
  text: string;
  value: string;
}

const api = (messagingServiceUrl: string) => getExternalApi(messagingServiceUrl) as AxiosInstance;

export const getTextTemplates = (
  messagingServiceUrl: string,
  { page = 1, perPage = RECORDS_PER_PAGE, search = "" }: TextTemplatesParams,
  signal?: AbortSignal,
): Promise<TextTemplatesResponse> => {
  const path = `${Math.max(page, 1)}/${perPage}`;
  const searchTerm = search.trim().slice(0, MAX_SEARCH_LENGTH);
  // The search endpoint requires a non-empty searchTerm, so fall back to the plain list
  const request = searchTerm
    ? api(messagingServiceUrl).post<MessagingPage<TextTemplate>>(
        `/TextTemplates/Search/${path}`,
        { searchTerm },
        { signal },
      )
    : api(messagingServiceUrl).get<MessagingPage<TextTemplate>>(`/TextTemplates/${path}`, { signal });

  return request.then(({ data }) => ({
      textTemplates: data.items || [],
      pagination: {
        currentPage: data.currentPage || 1,
        perPage: data.itemsPerPage,
        totalPages: Math.max(data.totalPages || 0, 1),
        totalEntries: data.totalItems,
      },
    }));
};

export const getTextPlaceholders = () =>
  caseTransformingAxios
    .get<{ placeholders: Placeholder[] }>(PLACEHOLDERS_URL)
    .then(({ data }) => data.placeholders);

export interface ParsedTextTemplate {
  message: string;
  unmatchedPlaceholders: string[];
}

export const parseTextTemplate = (message: string, recipientId: string, listingId?: string) =>
  caseTransformingAxios
    .post<ParsedTextTemplate>(PARSE_URL, { message, recipientId, listingId })
    .then(({ data }) => data);

export const saveTextTemplate = (
  messagingServiceUrl: string,
  { uuid, templateName, template, isShared = false }: TextTemplateDraft,
): Promise<void> => {
  const request = uuid
    ? api(messagingServiceUrl).put("/TextTemplates/Update", { uuid, name: templateName, template })
    : api(messagingServiceUrl).post("/TextTemplates/Create", { name: templateName, template, isShared });
  return request.then(() => undefined);
};

export const deleteTextTemplate = (messagingServiceUrl: string, uuid: string) =>
  api(messagingServiceUrl).delete(`/TextTemplates/${uuid}`);

const FIELD_LABELS: Record<string, string> = { name: "Name", template: "Message" };

interface ProblemDetails {
  title?: string;
  detail?: string;
  errors?: string[] | Record<string, string[]>;
}

export const errorMessages = (err: unknown): string[] => {
  const data = (err as { response?: { data?: ProblemDetails | string } } | null)?.response?.data;
  if (typeof data === "string" && data.trim()) return [data];
  if (!data || typeof data !== "object") return ["Something went wrong. Please try again."];

  const { errors, detail, title } = data;
  if (Array.isArray(errors)) {
    if (errors.length) return errors;
  } else if (errors && typeof errors === "object") {
    const messages = Object.entries(errors).flatMap(([field, fieldMessages]: [string, string[]]) => {
      const label = FIELD_LABELS[field.toLowerCase()];
      return fieldMessages.map((message) => (label ? `${label}: ${message}` : message));
    });
    if (messages.length) return messages;
  }
  return [detail || title || "Something went wrong. Please try again."];
};

export const errorMessage = (err: unknown) => errorMessages(err).join(", ");
